import { expandFacetMatches, trimFacetValue } from "./facet-match";

/** Home Search Sessions tab uses limit=22; Agenda search uses 10. Cap above both. */
export const PUBLIC_SESSIONS_V2_DEFAULT_PAGE_SIZE = 20;
export const PUBLIC_SESSIONS_V2_MAX_PAGE_SIZE = 50;
/** Non-search public lists (day agenda) stay slightly higher for infinite scroll. */
export const PUBLIC_SESSIONS_V2_LIST_MAX_PAGE_SIZE = 100;

export type SessionSearchTranslation = {
  name?: string | null;
  description?: string | null;
  location?: string | null;
  track?: string | null;
  type?: string | null;
  topics?: string[] | null;
  language?: { code?: string | null } | null;
};

export type SessionSearchRow = {
  id: string;
  startDate: Date;
  speakerUserIds: string[];
  topics?: string[] | null;
  translations: SessionSearchTranslation[];
};

export type SessionSearchFacets = {
  locations?: string[];
  tracks?: string[];
  types?: string[];
  topics?: string[];
};

/**
 * Trim, then drop regex metacharacters. Legacy Prisma `contains` treated the
 * stripped string as a case-insensitive substring. Whitespace-only input,
 * including `search=%20`, is not a search.
 */
export function normalizeSessionSearchQuery(query?: string | null): string {
  const trimmed = query?.trim() ?? "";
  if (!trimmed) return "";
  return trimmed.replace(/[.*+?^${}()|[\]\\]/g, "");
}

/** Case-insensitive substring. An empty needle matches nothing. */
export function sessionTextMatches(
  query: string,
  values: Array<string | null | undefined>
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return false;
  return values.some(
    (value) => typeof value === "string" && value.toLowerCase().includes(needle)
  );
}

export function normalizePublicSessionPagination(
  page?: number,
  limit?: number,
  maxPageSize: number = PUBLIC_SESSIONS_V2_MAX_PAGE_SIZE
): { page: number; take: number } {
  const safePage =
    typeof page === "number" && Number.isFinite(page) && page >= 1
      ? Math.floor(page)
      : 1;
  const rawLimit =
    typeof limit === "number" && Number.isFinite(limit)
      ? Math.floor(limit)
      : PUBLIC_SESSIONS_V2_DEFAULT_PAGE_SIZE;
  const take = Math.min(
    maxPageSize,
    Math.max(1, rawLimit || PUBLIC_SESSIONS_V2_DEFAULT_PAGE_SIZE)
  );
  return { page: safePage, take };
}

function translationFacetValues(
  translations: SessionSearchTranslation[],
  field: "location" | "track" | "type"
): Array<string | null> {
  return translations.map((translation) => translation[field] ?? null);
}

function sessionTopicValues(session: SessionSearchRow): string[] {
  const fromTranslations = session.translations.flatMap((translation) =>
    (translation.topics ?? []).map((topic) => topic.trim()).filter(Boolean)
  );
  const legacy = (session.topics ?? []).map((topic) => topic.trim()).filter(Boolean);
  return [...fromTranslations, ...legacy];
}

function facetRequestedMatches(
  stored: Array<string | null | undefined>,
  requested: string[] | undefined
): boolean {
  if (!requested?.length) return true;
  const matches = expandFacetMatches(stored, requested);
  if (matches.length) {
    return stored.some(
      (value) => value != null && matches.includes(value)
    );
  }
  // Fall back to trim-equal against the request when nothing was stored yet.
  const wanted = new Set(requested.map((value) => value.trim()).filter(Boolean));
  return stored.some((value) => value != null && wanted.has(value.trim()));
}

/**
 * Facet filters match the previous Prisma path: trim-equal against stored
 * translation location/track/type, and topics on translations or legacy
 * session.topics.
 */
export function sessionMatchesFacets(
  session: SessionSearchRow,
  facets: SessionSearchFacets = {}
): boolean {
  if (
    !facetRequestedMatches(
      translationFacetValues(session.translations, "location"),
      facets.locations
    )
  ) {
    return false;
  }
  if (
    !facetRequestedMatches(
      translationFacetValues(session.translations, "track"),
      facets.tracks
    )
  ) {
    return false;
  }
  if (
    !facetRequestedMatches(
      translationFacetValues(session.translations, "type"),
      facets.types
    )
  ) {
    return false;
  }
  if (facets.topics?.length) {
    const storedTopics = sessionTopicValues(session);
    if (!facetRequestedMatches(storedTopics, facets.topics)) {
      return false;
    }
  }
  return true;
}

/**
 * Text matches any translation name/description, or any speaker display
 * name (profile name for the event, else user name) — same fields the old
 * Prisma OR covered without a User collection scan.
 */
export function sessionMatchesSearch(
  session: SessionSearchRow,
  query: string,
  speakerNamesByUserId: Map<string, Array<string | null | undefined>>
): boolean {
  const needle = normalizeSessionSearchQuery(query);
  if (!needle) return false;

  const translationHits = session.translations.flatMap((translation) => [
    translation.name,
    translation.description,
  ]);
  if (sessionTextMatches(needle, translationHits)) {
    return true;
  }

  for (const speakerId of session.speakerUserIds) {
    const names = speakerNamesByUserId.get(speakerId);
    if (names && sessionTextMatches(needle, names)) {
      return true;
    }
  }
  return false;
}

export function filterSessionsForPublicSearch<T extends SessionSearchRow>(
  sessions: T[],
  query: string,
  speakerNamesByUserId: Map<string, Array<string | null | undefined>>,
  facets: SessionSearchFacets = {}
): T[] {
  const needle = normalizeSessionSearchQuery(query);
  if (!needle) return [];

  return sessions.filter(
    (session) =>
      sessionMatchesFacets(session, facets) &&
      sessionMatchesSearch(session, needle, speakerNamesByUserId)
  );
}

export function pageSessionRows<T>(
  rows: T[],
  page: number,
  take: number
): {
  pageRows: T[];
  total: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalPages: number;
} {
  const total = rows.length;
  const totalPages = Math.ceil(total / take) || 1;
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * take;
  return {
    pageRows: rows.slice(start, start + take),
    total,
    hasNextPage: start + take < total,
    hasPreviousPage: safePage > 1,
    totalPages,
  };
}

export function collectSpeakerUserIds(
  sessions: Array<{ speakerUserIds: string[] }>
): string[] {
  return [...new Set(sessions.flatMap((session) => session.speakerUserIds))];
}

export function trimFacetList(values?: string[]): string[] | undefined {
  if (!values?.length) return undefined;
  const trimmed = values
    .map((value) => trimFacetValue(value))
    .filter((value): value is string => Boolean(value));
  return trimmed.length ? trimmed : undefined;
}
